/**
 * World = シリアライズ可能な GameState ＋ 派生キャッシュ（跳躍グラフなど）。
 * 派生物はセーブしない。地形が変わった時だけ組み直す（§5.2）。
 */
import {
  BED,
  BUILT,
  EMPTY,
  POST,
  SHELF,
  buildIdAt,
  canPlaceDecor,
  decorAt,
  inBounds,
  isPlayerPlaced,
  isSolid,
  Grid,
  NO_FURNITURE,
  PLACED,
  canPlace,
  canPlaceFurniture,
  furnitureAt,
  keyOf,
  tileAt,
  TILE,
} from './grid';
import { JumpGraph, NodeKey, bfs, buildGraph, paramsKey } from './graph';
import {
  ItemDef,
  ItemId,
  itemAtIndex,
  itemCells,
  itemCost,
  itemDef,
  itemIndex,
  isSolidItem,
} from './items';
import {
  MATERIAL_IDS,
  MaterialBundle,
  addMaterials,
  hasMaterials,
  spendMaterials,
} from './materials';
import { addDropTo, dropAtIn } from './drops';
import { fireDiscovery } from './discovery';
import {
  BuildInstance,
  DropPile,
  FixtureInstance,
  FixtureKind,
  GameState,
  MemoryInstance,
  HISTORIES,
  SPECIES,
  SalvageKind,
  Species,
  fixtureKindOf,
  makeCat,
  salvageKindOf,
} from './state';
import { rngInt } from './rng';
import { ARRIVE_SEC, repairCat } from './cat';
import type { Comfort } from './comfort';
import type { FloorOpened } from './floors';

export type { ItemId } from './items';

export interface UndoEntry {
  /** 置いた操作か、外した操作か */
  kind: 'place' | 'remove';
  item: ItemId;
  c: number;
  r: number;
  flip: boolean;
  /** 操作前のタイル（1マス物のみ） */
  before: number;
  beforeFurniture: number;
  beforeDecor: number;
  /** 複数マスの構造物 */
  buildId?: number;
  rebuild?: BuildInstance;
}

/** 爪とぎの効き目が届く範囲（マス） */
export const POST_RANGE = 4;

/** これだけ傷むと壊れて資材に戻る（§3.5 ロスにしない） */
export const WEAR_LIMIT = 100;

/**
 * かけら（§10.8）。
 *
 * 資材が出るのが「完全に壊れた瞬間」だけだと、ピアノのような丈夫な物は
 * 何分も何も落ちず、画面を見ていても何が起きているのか分からない。
 * じゃれられている間にも、少しずつ**かけら**が落ちるようにしてある。
 * 落ちる場所も原因も同じ（§2.2 原因と症状は同じ場所に）。
 *
 * かけらは壊した時の取り分とは別の上乗せ。ロスにはしないという §3.5 の逆で、
 * 「猫がさんざん遊んだ物のほうが実入りが良い」という素直な形にしてある。
 */
export const CHIP_STEPS = 4;
export const FIXTURE_CHIP_STEPS = 2;

/** かけら1つぶん。その物から採れる資材を順ぐりに出す */
function chipOf(bundle: MaterialBundle, step: number): MaterialBundle {
  const ids = MATERIAL_IDS.filter((id) => (bundle[id] ?? 0) > 0);
  if (!ids.length) return {};
  return { [ids[step % ids.length]]: 1 };
}

/** 傷みが節目を越えるたびに、かけらを1つ落とす */
function dropChips(
  world: World,
  c: number,
  r: number,
  yieldOf: MaterialBundle,
  toughness: number,
  before: number,
  after: number,
  steps: number,
): void {
  const at = (w: number): number => Math.floor((w / toughness) * (steps + 1));
  for (let i = at(before) + 1; i <= Math.min(at(after), steps); i++) {
    addDrop(world, c, r, chipOf(yieldOf, i - 1));
  }
}

/** キャットタワーの形。items.json に移したが、名前で引きたい箇所が多いので残す */
export function towerCells(c: number, r: number, flip: boolean): { c: number; r: number }[] {
  return itemCells('tower', c, r, flip);
}

export interface World {
  state: GameState;
  /** 猫の種類ごとの跳躍グラフ（§3.2）。パラメータが同じなら共有する */
  graphs: Map<string, JumpGraph>;
  /** 実行中に書き換えられる種類テーブル（デバッグのスライダー用） */
  species: Map<string, Species>;
  /** 種類ごとの到達集合キャッシュ。地形変更で無効化 */
  reachCache: Map<string, Map<NodeKey, NodeKey | null>>;
  /**
   * ノードごとの通行回数。デバッグでどこが動線かを見るためだけのもの（§6）。
   * ゲーム状態ではないのでセーブしない。
   */
  traffic: Map<NodeKey, number>;
  /** 直近の快適条件の判定結果。派生物なのでセーブしない */
  lastComfort: Comfort | null;
  /** この tick で開通したフロア。描画が演出に使って空にする */
  justOpened: FloorOpened[];
  /** この tick で壊れた置き去りの物。描画が演出に使って空にする */
  justBroke: { key: NodeKey; kind: SalvageKind }[];
  /** この tick で猫が叩き落とした廃墟の飾り。描画が演出に使って空にする */
  justFell: { c: number; r: number; kind: FixtureKind }[];
  /** この tick で見つかった思い出。描画が演出に使って空にする */
  justFound: MemoryInstance[];
  undoStack: UndoEntry[];
  /** 左右反転して置くか（表示側の持ち物。机やキャットタワーで効く） */
  placeFlip: boolean;
  /** ステージ編集で掴んでいる思い出の id（§6）。ゲーム状態ではない */
  heldMemory: string | null;
  /** セーブが必要か */
  dirty: boolean;
}

export function createWorld(state: GameState): World {
  const species = new Map<string, Species>();
  for (const s of SPECIES) species.set(s.id, { ...s });
  // グラフは graphFor() で遅延構築する。
  // ここで repairCat を走らせてはいけない：ロードした状態が書き換わり、
  // 「セーブして再開した続き」が「そのまま回した場合」と一致しなくなる（§6 再現性）。
  return {
    state,
    graphs: new Map(),
    species,
    reachCache: new Map(),
    traffic: new Map(),
    lastComfort: null,
    justOpened: [],
    justBroke: [],
    justFell: [],
    justFound: [],
    undoStack: [],
    placeFlip: false,
    heldMemory: null,
    dirty: false,
  };
}

export function bumpTraffic(world: World, key: NodeKey): void {
  world.traffic.set(key, (world.traffic.get(key) ?? 0) + 1);
}

export function clearTraffic(world: World): void {
  world.traffic.clear();
}

export function speciesOf(world: World, id: string): Species {
  const s = world.species.get(id);
  if (!s) throw new Error(`unknown species: ${id}`);
  return s;
}

export function graphFor(world: World, speciesId: string): JumpGraph {
  const sp = speciesOf(world, speciesId);
  const key = paramsKey(sp);
  let g = world.graphs.get(key);
  if (!g) {
    g = buildGraph(world.state.grid, {
      maxUp: sp.maxUp,
      maxSide: sp.maxSide,
      maxFall: sp.maxFall,
      noNarrow: sp.noNarrow,
    });
    world.graphs.set(key, g);
  }
  return g;
}

/**
 * 地形が変わった時に必ず通る道。
 * 1. グラフを捨てる
 * 2. 全猫の経路を破棄する（§5.1 防御1）
 * 3. 立てなくなった猫を実在するノードへ引き戻す（§5.1 防御3）
 */
export function rebuildGraphs(world: World): void {
  world.graphs.clear();
  world.reachCache.clear();
  for (const cat of world.state.cats) {
    repairCat(world.state, graphFor(world, cat.species), cat);
  }
}

/** 種類ごとの到達集合。出発点はその種類の猫（居なければ餌場） */
export function reachableFor(world: World, speciesId: string): Map<NodeKey, NodeKey | null> {
  const cached = world.reachCache.get(speciesId);
  if (cached) return cached;

  const graph = graphFor(world, speciesId);
  const grid = world.state.grid;
  const starts = world.state.cats.filter((c) => c.species === speciesId).map((c) => c.node);
  if (starts.length === 0) starts.push(keyOf(grid, world.state.bowl.c, world.state.bowl.r));
  const reach = bfs(graph, starts);
  world.reachCache.set(speciesId, reach);
  return reach;
}

export type PlaceReason =
  | '資材がない'
  | '在庫にない'
  | '宙に浮く'
  | '猫がいる'
  | 'ここには置けない'
  | '床の上にしか置けない'
  | 'まだ作れない'
  | '場所が足りない'
  | '天井から吊るす物';

export type PlaceResult = { ok: true } | { ok: false; reason: PlaceReason };

function catOccupies(world: World, c: number, r: number): boolean {
  const cx = c * TILE + TILE / 2;
  const cy = (r + 1) * TILE;
  return world.state.cats.some(
    (k) => Math.abs(k.x - cx) < TILE * 0.7 && Math.abs(k.y - cy) < TILE * 0.9,
  );
}

// ---- クラフト（作る）と設置（置く）を分ける -----------------------------
//
// 作るのは資材があればいつでもできる。置き場所は後から決める。
// この2段構えのおかげで「素材が余ったから何か作る」と
// 「どこに置こうか眺める」が別の楽しみになる。

export function inventoryCount(state: GameState, item: ItemId): number {
  return state.inventory[item] ?? 0;
}

export function canCraft(world: World, item: ItemId): PlaceResult {
  if (!world.state.unlockedItems.includes(item)) return { ok: false, reason: 'まだ作れない' };
  if (!hasMaterials(world.state.materials, itemCost(item))) {
    return { ok: false, reason: '資材がない' };
  }
  return { ok: true };
}

/** 資材を使って1つ作る。できた物はインベントリに入る */
export function craft(world: World, item: ItemId): PlaceResult {
  const check = canCraft(world, item);
  if (!check.ok) return check;
  spendMaterials(world.state.materials, itemCost(item));
  world.state.inventory[item] = inventoryCount(world.state, item) + 1;
  world.dirty = true;
  return { ok: true };
}

/** 作った物をばらして資材に戻す。ロスにはしない（§3.5） */
export function scrap(world: World, item: ItemId): PlaceResult {
  if (inventoryCount(world.state, item) <= 0) return { ok: false, reason: '在庫にない' };
  world.state.inventory[item] = inventoryCount(world.state, item) - 1;
  addMaterials(world.state.materials, itemCost(item));
  world.dirty = true;
  return { ok: true };
}

function takeFromInventory(world: World, item: ItemId): boolean {
  const n = inventoryCount(world.state, item);
  if (n <= 0) return false;
  world.state.inventory[item] = n - 1;
  return true;
}

function returnToInventory(world: World, item: ItemId): void {
  world.state.inventory[item] = inventoryCount(world.state, item) + 1;
}

// ---- 置く -------------------------------------------------------------

/** 置けるか。置く前の下見（ゴースト表示）にも使う。判定は必ず sim 側（§4.4） */
export function canPlaceItem(
  world: World,
  c: number,
  r: number,
  item: ItemId,
  flip = world.placeFlip,
): PlaceResult {
  const grid = world.state.grid;
  const def = itemDef(item);

  if (def.kind === 'decor') {
    // 奥の壁に掛けるだけなので支えは要らない。通行にも一切関係しない
    if (!canPlaceDecor(grid, c, r)) return { ok: false, reason: 'ここには置けない' };
    return { ok: true };
  }

  if (def.kind === 'comfort') {
    if (furnitureAt(grid, c, r) !== NO_FURNITURE) return { ok: false, reason: 'ここには置けない' };
    if (!canPlaceFurniture(grid, c, r)) return { ok: false, reason: '床の上にしか置けない' };
    return { ok: true };
  }

  if (def.kind === 'build') {
    for (const cell of itemCells(item, c, r, flip)) {
      if (!inBounds(grid, cell.c, cell.r)) return { ok: false, reason: '場所が足りない' };
      if (tileAt(grid, cell.c, cell.r) !== EMPTY) return { ok: false, reason: '場所が足りない' };
      if (furnitureAt(grid, cell.c, cell.r) !== NO_FURNITURE) {
        return { ok: false, reason: '場所が足りない' };
      }
      if (catOccupies(world, cell.c, cell.r)) return { ok: false, reason: '猫がいる' };
    }
    // 足元さえ固ければ自分で立つ
    if (!isSolid(grid, c, r + 1)) return { ok: false, reason: '宙に浮く' };
    return { ok: true };
  }

  // platform
  if (tileAt(grid, c, r) !== EMPTY) return { ok: false, reason: 'ここには置けない' };
  if (furnitureAt(grid, c, r) !== NO_FURNITURE) return { ok: false, reason: 'ここには置けない' };
  if (catOccupies(world, c, r)) return { ok: false, reason: '猫がいる' };
  if (item === 'hang') {
    // 吊り棚は天井から下げる。下に支えが要らないかわりに、上が天井であること
    if (tileAt(grid, c, r - 1) !== EMPTY) return { ok: false, reason: '天井から吊るす物' };
    if (!isSolid(grid, c, r - 2)) return { ok: false, reason: '天井から吊るす物' };
    return { ok: true };
  }
  if (!canPlace(grid, c, r)) return { ok: false, reason: '宙に浮く' };
  return { ok: true };
}

/** インベントリの物を置く。無ければ置けない（先に craft する） */
export function place(world: World, c: number, r: number, item: ItemId = 'box'): PlaceResult {
  const grid = world.state.grid;
  const def = itemDef(item);
  const flip = world.placeFlip;

  if (inventoryCount(world.state, item) <= 0) return { ok: false, reason: '在庫にない' };
  const check = canPlaceItem(world, c, r, item, flip);
  if (!check.ok) return check;

  const k = keyOf(grid, c, r);
  const undo: UndoEntry = {
    kind: 'place',
    item,
    c,
    r,
    flip,
    before: tileAt(grid, c, r),
    beforeFurniture: furnitureAt(grid, c, r),
    beforeDecor: decorAt(grid, c, r),
  };

  if (def.kind === 'decor') {
    grid.decor[k] = itemIndex(item);
    takeFromInventory(world, item);
    world.undoStack.push(undo);
    world.dirty = true;
    return { ok: true };
  }

  if (def.kind === 'comfort') {
    grid.furniture[k] = item === 'post' ? POST : BED;
    grid.wear[k] = 0;
    takeFromInventory(world, item);
    world.undoStack.push(undo);
    world.dirty = true;
    return { ok: true };
  }

  if (def.kind === 'build') {
    const id = world.state.nextBuildId++;
    for (const cell of itemCells(item, c, r, flip)) {
      const kk = keyOf(grid, cell.c, cell.r);
      grid.tiles[kk] = BUILT;
      grid.build[kk] = id;
      grid.wear[kk] = 0;
    }
    world.state.builds.push({ id, item, c, r, flip });
    takeFromInventory(world, item);
    world.undoStack.push({ ...undo, buildId: id });
    rebuildGraphs(world);
    world.dirty = true;
    return { ok: true };
  }

  grid.tiles[k] = item === 'box' ? PLACED : SHELF;
  grid.wear[k] = 0;
  takeFromInventory(world, item);
  world.undoStack.push(undo);
  rebuildGraphs(world);
  world.dirty = true;
  return { ok: true };
}

/** そのマスにある物をどける。飾り → 家具 → 足場 の順に見る */
export function remove(world: World, c: number, r: number): PlaceResult {
  const grid = world.state.grid;
  const k = keyOf(grid, c, r);

  const decor = itemAtIndex(decorAt(grid, c, r));
  if (decor) {
    grid.decor[k] = 0;
    returnToInventory(world, decor.id);
    world.undoStack.push({
      kind: 'remove',
      item: decor.id,
      c,
      r,
      flip: false,
      before: tileAt(grid, c, r),
      beforeFurniture: furnitureAt(grid, c, r),
      beforeDecor: itemIndex(decor.id),
    });
    world.dirty = true;
    return { ok: true };
  }

  const furn = furnitureAt(grid, c, r);
  if (furn !== NO_FURNITURE) {
    const item: ItemId = furn === POST ? 'post' : 'bed';
    grid.furniture[k] = NO_FURNITURE;
    grid.wear[k] = 0;
    returnToInventory(world, item);
    world.undoStack.push({
      kind: 'remove',
      item,
      c,
      r,
      flip: false,
      before: tileAt(grid, c, r),
      beforeFurniture: furn,
      beforeDecor: 0,
    });
    world.dirty = true;
    return { ok: true };
  }

  // 複数マスの構造物は、どのマスを押してもまるごと外れる
  const buildId = buildIdAt(grid, c, r);
  if (buildId > 0) return removeBuild(world, buildId);

  if (!isPlayerPlaced(grid, c, r)) return { ok: false, reason: 'ここには置けない' };
  const was = tileAt(grid, c, r);
  const item: ItemId = was === SHELF ? 'shelf' : 'box';
  grid.tiles[k] = EMPTY;
  grid.wear[k] = 0;
  returnToInventory(world, item);
  world.undoStack.push({
    kind: 'remove',
    item,
    c,
    r,
    flip: false,
    before: was,
    beforeFurniture: NO_FURNITURE,
    beforeDecor: 0,
  });
  rebuildGraphs(world);
  world.dirty = true;
  return { ok: true };
}

function clearBuild(world: World, build: BuildInstance): void {
  const grid = world.state.grid;
  for (const cell of itemCells(build.item as ItemId, build.c, build.r, build.flip)) {
    const k = keyOf(grid, cell.c, cell.r);
    if (grid.build[k] !== build.id) continue;
    grid.tiles[k] = EMPTY;
    grid.build[k] = 0;
    grid.wear[k] = 0;
  }
}

function paintBuild(world: World, build: BuildInstance): void {
  const grid = world.state.grid;
  for (const cell of itemCells(build.item as ItemId, build.c, build.r, build.flip)) {
    const k = keyOf(grid, cell.c, cell.r);
    grid.tiles[k] = BUILT;
    grid.build[k] = build.id;
  }
}

function removeBuild(world: World, id: number): PlaceResult {
  const build = world.state.builds.find((b) => b.id === id);
  if (!build) return { ok: false, reason: 'ここには置けない' };
  clearBuild(world, build);
  world.state.builds = world.state.builds.filter((b) => b.id !== id);
  returnToInventory(world, build.item as ItemId);
  world.undoStack.push({
    kind: 'remove',
    item: build.item as ItemId,
    c: build.c,
    r: build.r,
    flip: build.flip,
    before: EMPTY,
    beforeFurniture: NO_FURNITURE,
    beforeDecor: 0,
    rebuild: build,
  });
  rebuildGraphs(world);
  world.dirty = true;
  return { ok: true };
}

/** 誤タップ対策（§5.3）。設置・撤去のどちらも1手ずつ戻せる。 */
export function undo(world: World): boolean {
  const e = world.undoStack.pop();
  if (!e) return false;
  const grid = world.state.grid;
  const def = itemDef(e.item);

  if (e.kind === 'place') {
    // 置いたのを取り消す。物はインベントリに返る
    if (e.buildId !== undefined) {
      const build = world.state.builds.find((b) => b.id === e.buildId);
      if (build) {
        clearBuild(world, build);
        world.state.builds = world.state.builds.filter((b) => b.id !== e.buildId);
      }
      returnToInventory(world, e.item);
      rebuildGraphs(world);
      world.dirty = true;
      return true;
    }
    const k = keyOf(grid, e.c, e.r);
    const tileChanged = grid.tiles[k] !== e.before;
    grid.tiles[k] = e.before;
    grid.furniture[k] = e.beforeFurniture;
    grid.decor[k] = e.beforeDecor;
    returnToInventory(world, e.item);
    if (tileChanged) rebuildGraphs(world);
    world.dirty = true;
    return true;
  }

  // 外したのを取り消す。物はインベントリから出て、元の場所に戻る
  if (!takeFromInventory(world, e.item)) return false;
  if (e.rebuild) {
    paintBuild(world, e.rebuild);
    world.state.builds.push(e.rebuild);
    rebuildGraphs(world);
    world.dirty = true;
    return true;
  }
  const k = keyOf(grid, e.c, e.r);
  if (def.kind === 'decor') {
    grid.decor[k] = e.beforeDecor;
  } else if (def.kind === 'comfort') {
    grid.furniture[k] = e.beforeFurniture;
  } else {
    grid.tiles[k] = e.before;
    rebuildGraphs(world);
  }
  world.dirty = true;
  return true;
}

// ---- 落ちている資材（§10.8）--------------------------------------------
//
// 猫は資材を拾わない。落ちたまま、プレイヤーが拾うまでそこにある。
// 消えないし腐らないので、拾い忘れても損はしない（§2.4 急かさない）。

/** 落ちている資材を足す。同じマスのものはひと山にまとめる（タップ1回で拾えるように） */
export function addDrop(world: World, c: number, r: number, mats: MaterialBundle): void {
  addDropTo(world.state, c, r, mats);
  world.dirty = true;
}

export function dropAt(world: World, c: number, r: number): DropPile | null {
  return dropAtIn(world.state, c, r);
}

/**
 * 落ちている資材を拾う。ひと山まるごと。
 * これがプレイヤーの数少ない直接操作で、猫には頼めない（§2.3 の裏返し）。
 */
export function pickUp(world: World, c: number, r: number): MaterialBundle | null {
  const pile = dropAt(world, c, r);
  if (!pile) return null;
  addMaterials(world.state.materials, pile.mats);
  world.state.drops = world.state.drops.filter((d) => d !== pile);
  world.dirty = true;
  return pile.mats;
}

/** 拾い漏らしを減らすため、押したマスの周り1マスまで見る */
export function pickUpNear(world: World, c: number, r: number): MaterialBundle | null {
  const exact = pickUp(world, c, r);
  if (exact) return exact;
  for (const [dc, dr] of [
    [0, 1],
    [-1, 0],
    [1, 0],
    [0, -1],
  ] as const) {
    const got = pickUp(world, c + dc, r + dr);
    if (got) return got;
  }
  return null;
}

// ---- 傷み・破壊 --------------------------------------------------------

/** 近くに爪とぎがあるか（§3.7 食堂の報酬）。あると設置物が傷みにくい */
export function nearPost(world: World, c: number, r: number): boolean {
  const grid = world.state.grid;
  for (let dc = -POST_RANGE; dc <= POST_RANGE; dc++) {
    for (let dr = -1; dr <= 1; dr++) {
      if (furnitureAt(grid, c + dc, r + dr) === POST) return true;
    }
  }
  return false;
}

function itemOfTile(grid: Grid, c: number, r: number): ItemDef | null {
  const tile = tileAt(grid, c, r);
  if (tile === PLACED) return itemDef('box');
  if (tile === SHELF) return itemDef('shelf');
  if (furnitureAt(grid, c, r) === BED) return itemDef('bed');
  return null;
}

/**
 * 設置物を傷ませる（§3.5）。
 * よく壊れる場所＝猫が集中している、またはストレスが溜まっている場所。
 * 統計画面を見なくても部屋の荒れ具合で混雑が読めるようにするための仕組み。
 *
 * 壊れた物は資材に戻る。ただし在庫に直接は入らず、**その場に落ちる**（§10.8）。
 * 壊れると経路が塞がることがあるが、それは §5.1 の経路破棄で面倒を見る。
 */
export function wearItem(world: World, c: number, r: number, amount: number): boolean {
  const grid = world.state.grid;
  const k = keyOf(grid, c, r);
  const def = itemOfTile(grid, c, r);
  if (!def || def.wearFactor <= 0) return false;

  // 爪とぎが近くにあれば、猫はそっちで爪をとぐので傷みが減る
  let factor = def.wearFactor;
  if (nearPost(world, c, r)) factor *= 0.25;
  grid.wear[k] += amount * factor;
  if (grid.wear[k] < WEAR_LIMIT) return false;

  // 壊れた
  grid.wear[k] = 0;
  if (def.id === 'bed') {
    grid.furniture[k] = NO_FURNITURE;
  } else {
    grid.tiles[k] = EMPTY;
    rebuildGraphs(world); // 足場が消えたので、猫の経路を捨てる（§5.1）
  }
  addDrop(world, c, r, def.cost);
  // 壊れた場所には跡が残る
  if (k < world.state.traces.length) world.state.traces[k] += 2;
  world.dirty = true;
  return true;
}

/**
 * 置き去りの物を猫がじゃれて壊す（§3.4 / §3.5）。
 * これがこのゲームの資材の主な出所。
 *
 *   猫が勝手に動く → 元からある物を壊す → 資材が床に落ちる
 *   → プレイヤーが拾う → クラフトして飾る／置く
 *   → 猫の行ける範囲が広がる → その先にまた手つかずの物がある
 *
 * プレイヤーは壊す物を選べない（§2.3）。決めるのは拾った後の使い道だけ。
 */
export function wearSalvage(world: World, key: NodeKey, amount: number): SalvageKind | null {
  const grid = world.state.grid;
  const kindIndex = grid.salvage[key];
  const kind = salvageKindOf(kindIndex);
  if (!kind) return null;

  const c = key % grid.cols;
  const r = (key / grid.cols) | 0;
  const before = grid.wear[key];
  grid.wear[key] += amount;
  // 壊れる前でも、じゃれられている間はかけらが落ちる
  dropChips(world, c, r, kind.yield, kind.toughness, before, grid.wear[key], CHIP_STEPS);
  if (grid.wear[key] < kind.toughness) return null;

  // 壊れた。残りの資材がまとめてその場に落ちる
  grid.salvage[key] = 0;
  grid.wear[key] = 0;
  grid.tiles[key] = EMPTY;
  if (key < world.state.traces.length) world.state.traces[key] += 4;
  rebuildGraphs(world); // 足場が消えたので経路を捨てる（§5.1）
  addDrop(world, c, r, kind.yield);
  // 壊した物の中から、廃墟になる前の物が出ることがある（§10.18）
  const found = fireDiscovery(world.state, { on: 'break', salvage: kind.id, c, r });
  if (found) world.justFound.push(found);
  world.dirty = true;
  world.justBroke.push({ key, kind });
  return kind;
}

// ---- 廃墟の飾り（高い所の資材）------------------------------------------

export function fixtureAt(world: World, c: number, r: number): FixtureInstance | null {
  return world.state.fixtures.find((f) => f.c === c && f.r === r) ?? null;
}

export function fixtureById(world: World, id: number): FixtureInstance | null {
  return world.state.fixtures.find((f) => f.id === id) ?? null;
}

/**
 * そのマスに立っている猫の前足が届く飾り（§3.3）。
 * 届く高さは種類ごとに違う。跳ぶのが得意な猫ほど高い物を落とせる。
 *
 * 届かない猫のために、プレイヤーが**ちょうどいい高さの台**を作って置く。
 * これが「同じ棚が、入居者によって意味を変える」の飾り版（§3.3）。
 */
export function fixturesInReach(world: World, node: NodeKey, paw: number): FixtureInstance[] {
  const grid = world.state.grid;
  const c = node % grid.cols;
  const r = (node / grid.cols) | 0;
  return world.state.fixtures.filter(
    (f) => Math.abs(f.c - c) <= 1 && r - f.r >= 0 && r - f.r <= paw,
  );
}

/** 猫が飾りを叩く。落ちたら資材になって床まで落ちる */
export function wearFixture(world: World, id: number, amount: number): FixtureKind | null {
  const fixture = fixtureById(world, id);
  if (!fixture) return null;
  const kind = fixtureKindOf(fixture.kind);
  if (!kind) return null;

  const before = fixture.wear;
  fixture.wear += amount;
  dropChips(
    world,
    fixture.c,
    fixture.r,
    kind.yield,
    kind.toughness,
    before,
    fixture.wear,
    FIXTURE_CHIP_STEPS,
  );
  if (fixture.wear < kind.toughness) return null;

  world.state.fixtures = world.state.fixtures.filter((f) => f.id !== id);
  addDrop(world, fixture.c, fixture.r, kind.yield);
  const found = fireDiscovery(world.state, {
    on: 'swat',
    fixture: kind.id,
    c: fixture.c,
    r: fixture.r,
  });
  if (found) world.justFound.push(found);
  world.justFell.push({ c: fixture.c, r: fixture.r, kind });
  world.dirty = true;
  return kind;
}

/** 猫が近づける「じゃれられる物」を探す。戻り値は salvage のマス */
export function toysNear(world: World, key: NodeKey): NodeKey[] {
  const grid = world.state.grid;
  const c = key % grid.cols;
  const r = (key / grid.cols) | 0;
  const out: NodeKey[] = [];
  for (const [dc, dr] of [
    [-1, 0],
    [1, 0],
    [0, 1],
  ] as const) {
    const k = keyOf(grid, c + dc, r + dr);
    if (grid.salvage[k] > 0) out.push(k);
  }
  return out;
}

// ---- 猫の出入り --------------------------------------------------------

/** 外から猫が来る（§3.6）。到着は必ず絵で見せるので、演出用の残り秒を持たせる */
export function arriveNewCat(world: World, speciesId: string): void {
  const state = world.state;
  const cat = makeCat(state.grid, state.nextCatId++, speciesId, state.entrance.c, state.entrance.r);
  // 来歴は一行だけ付く（§1 裏テーマ）。説明はしない
  const entry = HISTORIES[rngInt(state.rng, HISTORIES.length)];
  cat.history = entry.line;
  cat.mark = entry.mark;
  cat.arriving = ARRIVE_SEC;
  cat.face = -1;
  cat.wait = ARRIVE_SEC;
  state.cats.push(cat);
  world.reachCache.clear();
  world.dirty = true;
}

/** デバッグ用。任意の場所に猫を足す（§6） */
export function addCat(world: World, speciesId: string, c: number, r: number): void {
  const cat = makeCat(world.state.grid, world.state.nextCatId++, speciesId, c, r);
  world.state.cats.push(cat);
  world.reachCache.clear();
  repairCat(world.state, graphFor(world, speciesId), cat);
  world.dirty = true;
}

export function removeCat(world: World, id: number): void {
  world.state.cats = world.state.cats.filter((c) => c.id !== id);
  world.reachCache.clear();
  world.dirty = true;
}

/** デバッグのスライダーから跳躍パラメータを変える（§6） */
export function setSpeciesParams(world: World, id: string, patch: Partial<Species>): void {
  const sp = speciesOf(world, id);
  world.species.set(id, { ...sp, ...patch });
  world.graphs.clear();
  world.reachCache.clear();
  for (const cat of world.state.cats) {
    if (cat.species === id) repairCat(world.state, graphFor(world, id), cat);
  }
}

export function gridOf(world: World): Grid {
  return world.state.grid;
}

export { isSolidItem };
